// ===== BEGIN FILE: src/components/HousePlanetInterpretations.tsx =====
import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Sheet, Table, Button, Chip, IconButton, Tooltip } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { supabase } from '../utils/supabaseClient';
import { RASHI_NAMES, NAKSHATRA_NAMES, OWN_SIGNS, type CombustionMap } from '../utils/astroEngine';
import { getPlanetsAspectingHouse, type PlanetsInHouses } from '../utils/parashariAspects';

type Planet = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn' | 'Rahu' | 'Ketu';

// ===== SESSION CACHE (localStorage + memory, 24-hour expiry) =====
const CACHE_VERSION = 'v1';
const CACHE_KEY_HOUSES = 'interp_houses_v1';
const CACHE_KEY_PLANETS = 'interp_planets_v1';
const CACHE_KEY_LIH = 'interp_lih_v1';
const CACHE_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 1 year (permanent - static reference data)

const MEMORY_CACHE = {
  houseMeta: null as any[] | null,
  planetMeta: null as any[] | null,
  lordInHousePredictions: null as Map<number, any> | null,
  fetchPromise: null as Promise<void> | null,
  isFetching: false // Synchronous lock flag
};

function loadFromLocalStorage<T>(key: string): T | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (Date.now() - parsed.timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.data as T;
  } catch {
    return null;
  }
}

function saveToLocalStorage(key: string, data: any): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // Quota exceeded or disabled - continue without caching
  }
}

async function ensureInterpretationDataCached(): Promise<void> {
  // Check memory cache first
  if (MEMORY_CACHE.houseMeta && MEMORY_CACHE.planetMeta && MEMORY_CACHE.lordInHousePredictions) {
    return;
  }

  // Check localStorage
  const housesLS = loadFromLocalStorage<any[]>(CACHE_KEY_HOUSES);
  const planetsLS = loadFromLocalStorage<any[]>(CACHE_KEY_PLANETS);
  const lihLS = loadFromLocalStorage<Array<[number, any]>>(CACHE_KEY_LIH);

  if (housesLS && planetsLS && lihLS) {
    MEMORY_CACHE.houseMeta = housesLS;
    MEMORY_CACHE.planetMeta = planetsLS;
    MEMORY_CACHE.lordInHousePredictions = new Map(lihLS);
    return;
  }

  // If fetch in progress, await it
  if (MEMORY_CACHE.isFetching && MEMORY_CACHE.fetchPromise) {
    return MEMORY_CACHE.fetchPromise;
  }

  // Set synchronous lock IMMEDIATELY to prevent race conditions
  if (MEMORY_CACHE.isFetching) {
    // Another instance set the flag but hasn't created promise yet - wait a bit
    await new Promise(resolve => setTimeout(resolve, 50));
    return ensureInterpretationDataCached(); // Retry
  }

  MEMORY_CACHE.isFetching = true; // Lock acquired

  // Fetch from database (or localStorage cache)
  MEMORY_CACHE.fetchPromise = (async () => {
    try {
      const housesCacheKey = 'houses_full_v1';
      const planetsCacheKey = 'planets_all_v1';
      
      // Try localStorage cache first (365 days)
      let hm: any[] | null = null;
      let pm: any[] | null = null;
      
      const housesStr = localStorage.getItem(housesCacheKey);
      if (housesStr) {
        try {
          const parsed = JSON.parse(housesStr);
          const age = Date.now() - (parsed.timestamp || 0);
          if (age < 365 * 24 * 60 * 60 * 1000) {
            hm = parsed.data;
          }
        } catch (e) { }
      }
      
      const planetsStr = localStorage.getItem(planetsCacheKey);
      if (planetsStr) {
        try {
          const parsed = JSON.parse(planetsStr);
          const age = Date.now() - (parsed.timestamp || 0);
          if (age < 365 * 24 * 60 * 60 * 1000) {
            pm = parsed.data;
          }
        } catch (e) { }
      }
      
      if (!hm || !pm) {
        const [{ data: hmData, error: houseErr }, { data: pmData, error: planetErr }] = await Promise.all([
          supabase.from('houses').select('id, name, description, note_planet_sun, note_planet_moon, note_planet_mars, note_planet_mercury, note_planet_jupiter, note_planet_venus, note_planet_saturn, note_planet_rahu, note_planet_ketu, note_sign_aries, note_sign_taurus, note_sign_gemini, note_sign_cancer, note_sign_leo, note_sign_virgo, note_sign_libra, note_sign_scorpio, note_sign_sagittarius, note_sign_capricorn, note_sign_aquarius, note_sign_pisces, actionable_sun, actionable_moon, actionable_mars, actionable_mercury, actionable_jupiter, actionable_venus, actionable_saturn, actionable_rahu, actionable_ketu, caution_sun, caution_moon, caution_mars, caution_mercury, caution_jupiter, caution_venus, caution_saturn, caution_rahu, caution_ketu, remedy_sun, remedy_moon, remedy_mars, remedy_mercury, remedy_jupiter, remedy_venus, remedy_saturn, remedy_rahu, remedy_ketu').order('id', { ascending: true }),
          supabase.from('planets').select('id, name').order('id', { ascending: true })
        ]);
        if (houseErr) throw houseErr;
        if (planetErr) throw planetErr;
        
        hm = hmData;
        pm = pmData;
        
        // Save to localStorage
        if (hm) localStorage.setItem(housesCacheKey, JSON.stringify({ data: hm, timestamp: Date.now() }));
        if (pm) localStorage.setItem(planetsCacheKey, JSON.stringify({ data: pm, timestamp: Date.now() }));
      }

      const lihResults = await Promise.all(Array.from({ length: 12 }, (_, i) => i + 1).map(async (destHouse) => {
        const { data, error } = await supabase
          .from('house_lord_in_house_predictions')
          .select('overview, strengths, challenges, remedies, dest_house_id, house_lord_house_id, lord1_pred, lord2_pred, lord3_pred, lord4_pred, lord5_pred, lord6_pred, lord7_pred, lord8_pred, lord9_pred, lord10_pred, lord11_pred, lord12_pred')
          .eq('dest_house_id', destHouse)
          .limit(1)
          .maybeSingle();
        return { destHouse, row: (error ? null : data) as any };
      }));

      const lihMap = new Map<number, any>();
      lihResults.forEach(({ destHouse, row }) => {
        if (row) lihMap.set(destHouse, row);
      });

      // Store in memory
      MEMORY_CACHE.houseMeta = hm;
      MEMORY_CACHE.planetMeta = pm;
      MEMORY_CACHE.lordInHousePredictions = lihMap;

      // Store in localStorage (convert Map to array for serialization)
      saveToLocalStorage(CACHE_KEY_HOUSES, hm);
      saveToLocalStorage(CACHE_KEY_PLANETS, pm);
      saveToLocalStorage(CACHE_KEY_LIH, Array.from(lihMap.entries()));
    } finally {
      MEMORY_CACHE.fetchPromise = null;
      MEMORY_CACHE.isFetching = false; // Release lock
    }
  })();

  return MEMORY_CACHE.fetchPromise;
}

export type HousePlanetInterpretationsProps = {
  /** Sidereal ascendant longitude in degrees (0..360) */
  ascLonD1: number;
  /** Whole-sign cusps array (12 numbers, deg) */
  cuspsD1: number[];
  /** Planet longitudes (sidereal, deg) and retro flags */
  grahaLon: Partial<Record<Planet, { lon: number; retro?: boolean }>>;
  /** Planet placements in D1: house and offset */
  placementsD1: Record<Planet, { house: number; offsetDeg: number }>;
  /** Color resolver for planets (functional nature) */
  getPlanetColor?: (planet: Planet) => string;
  /** Combustion map */
  combustMap?: CombustionMap;
  /** Debilitation map */
  debilMap?: Partial<Record<Planet, boolean>>;
  /** Exaltation map */
  exaltMap?: Partial<Record<Planet, boolean>>;
  /** Blended percentages for planets (-100 to +100) */
  blendedPct?: Partial<Record<Planet, number>>;
  /** House scores (bhava bala or influence scores) */
  houseScores?: Partial<Record<number, number>>;
  /** Kaal Sarp Yoga detection */
  kaalSarpYoga?: {
    isPresent?: boolean;
    present?: boolean;
    type?: string;
    severity?: 'partial' | 'full';
    details?: string;
    specificType?: string;
    specificEffects?: string;
    rahuHouse?: number;
    ketuHouse?: number;
  } | null;
  /** Mangal Dosha detection */
  mangalDosha?: {
    present: boolean;
    severity?: 'low' | 'medium' | 'high' | 'cancelled';
    houses?: number[];
  };
  /** Sade Sati status */
  sadeSati?: {
    isActive: boolean;
    currentPhase?: string;
    from?: string;
    to?: string;
  } | null;
  /** Planet conjunctions with effects */
  conjunctions?: Array<{
    planets: Planet[];
    house: number;
    effect?: string;
    score?: number;
  }>;
};

type HouseRow = {
  house: number;
  signId: number; // 1..12
  signName: string;
  houseName?: string;
  houseDescription?: string | null;
  signInHouseNote?: string | null;
  signLord?: Planet | null;
  signLordNakshatra?: string | null;
  signLordNakshatraNote?: string | null;
  aspectingPlanets?: Array<{ planet: Planet; nakshatra?: string }>; // planets aspecting this house
  houseLordshipInfo?: Array<{ lordHouseId: number; destHouse?: number; pred?: string | null; overview?: string | null; strengths?: string | null; challenges?: string | null; remedies?: string | null }>; // where THIS house's lord is placed
  houseScore?: number; // bhava bala or influence score
  planets: Array<{
    planet: Planet;
    nakshatra?: string;
    dignities?: string[]; // exalted, debilitated, own sign, etc.
    housePlanetNote?: string | null;
    planetInHouse?: { overview?: string | null; nakText?: string | null; nakLordText?: string | null } | null;
    saturnConjunction?: { conjunctWith: Planet; effect: string; score: number } | null; // Saturn conjunction info
    blendedPct?: number; // -100 to +100
  }>;
  // House-level predictions (from houses table)
  housePredictions?: {
    actionables: Partial<Record<Planet, string>>;
    cautions: Partial<Record<Planet, string>>;
    remedies: Partial<Record<Planet, string>>;
  };
};

const PLANETS_7: Exclude<Planet, 'Rahu' | 'Ketu'>[] = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'];

// Classical sign lordship: index 0 = Aries (sign 1), index 11 = Pisces (sign 12)
const SIGN_LORDS: Exclude<Planet, 'Rahu' | 'Ketu'>[] = [
  'Mars',     // Aries (1)
  'Venus',    // Taurus (2)
  'Mercury',  // Gemini (3)
  'Moon',     // Cancer (4)
  'Sun',      // Leo (5)
  'Mercury',  // Virgo (6)
  'Venus',    // Libra (7)
  'Mars',     // Scorpio (8)
  'Jupiter',  // Sagittarius (9)
  'Saturn',   // Capricorn (10)
  'Saturn',   // Aquarius (11)
  'Jupiter',  // Pisces (12)
];

const nakToColumn: Record<string, string> = {
  Ashwini: 'nak_ashwini',
  Bharani: 'nak_bharani',
  Krittika: 'nak_krittika',
  Rohini: 'nak_rohini',
  'Mrigashīrsha': 'nak_mrigashira',
  'Ārdrā': 'nak_ardra',
  Punarvasū: 'nak_punarvasu',
  Pushyami: 'nak_pushya',
  Ashleṣā: 'nak_ashlesha',
  Maghā: 'nak_magha',
  PūrvaPhalgunī: 'nak_purva_phalguni',
  Uttaraphalgunī: 'nak_uttara_phalguni',
  Hasta: 'nak_hasta',
  Chitrā: 'nak_chitra',
  Svāti: 'nak_swati',
  Viśākhā: 'nak_vishakha',
  Anurādhā: 'nak_anuradha',
  Jyeṣṭhā: 'nak_jyeshtha',
  Mūla: 'nak_mula',
  PūrvaAshāḍhā: 'nak_purva_ashadha',
  UttarāAshāḍhā: 'nak_uttara_ashadha',
  Śravaṇā: 'nak_shravana',
  Dhanisthā: 'nak_dhanishta',
  Śatabhiṣaj: 'nak_shatabhisha',
  PūrvaBhādrapadā: 'nak_purva_bhadrapada',
  Uttarabhādrapadā: 'nak_uttara_bhadrapada',
  Revatī: 'nak_revati',
};

function ascSignFromAscLon(ascLonDeg: number): number {
  return Math.floor(((ascLonDeg % 360) + 360) % 360 / 30) + 1; // 1..12
}

function houseSign(ascSign1to12: number, house: number): number {
  return ((ascSign1to12 + house - 2 + 12) % 12) + 1; // 1..12
}

function getNakshatraNameFromLon(lon: number | undefined): string | undefined {
  if (typeof lon !== 'number' || !Number.isFinite(lon)) return undefined;
  const idx0 = Math.floor((((lon % 360) + 360) % 360) / (360 / 27));
  return NAKSHATRA_NAMES[idx0] as string;
}

/**
 * Parse conditional statements in prediction text and render with conditional styling
 * Identifies various patterns like "if Moon in 4th House", "Venus and Mars in the 4th House",
 * "Moon with Rahu", "If malefic Saturn", etc.
 * Returns array of text segments with styling flags
 */
function parseConditionalText(
  text: string,
  placementsD1: Record<string, { house: number; offsetDeg: number }>,
  currentHouse: number,
  allAspects?: ReturnType<typeof getPlanetsAspectingHouse>[]
): Array<{ text: string; isTrue?: boolean; isConditional?: boolean }> {
  // Natural malefics
  const MALEFICS = ['Mars', 'Saturn', 'Rahu', 'Ketu'];
  
  // Helper to check if a planet is afflicted by malefic aspects
  const isPlanetAfflicted = (planet: string): boolean => {
    if (!allAspects) return false;
    const planetHouse = placementsD1[planet]?.house;
    if (!planetHouse) return false;
    
    // Get aspects to the house where planet is placed
    const aspectsToHouse = allAspects[planetHouse - 1] || [];
    // Check if any malefic is aspecting this house
    return aspectsToHouse.some(asp => MALEFICS.includes(asp.from));
  };
  
  // Helper to check if planets are in same house
  const arePlanetsInSameHouse = (planets: string[]): { inSame: boolean; house?: number } => {
    if (planets.length === 0) return { inSame: false };
    const houses = planets.map(p => placementsD1[p]?.house).filter(h => h !== undefined);
    if (houses.length !== planets.length) return { inSame: false };
    const firstHouse = houses[0];
    const allSame = houses.every(h => h === firstHouse);
    return { inSame: allSame, house: allSame ? firstHouse : undefined };
  };

  // Expanded regex patterns to match various conditional statements
  // Note: [.,;!?]* at the end captures trailing punctuation
  const patterns = [
    // "if Planet is afflicted" or "afflicted Planet" or "Planet is afflicted by Planet"
    { regex: /if\s+(?:the\s+)?([A-Z][a-z]+)\s+is\s+afflicted(?:\s+by\s+([A-Z][a-z]+))?[.,;!?]*/gi, type: 'afflicted' },
    { regex: /afflicted\s+([A-Z][a-z]+)[.,;!?]*/gi, type: 'afflictedPrefix' },
    
    // "if malefic/benefic Planet" - Note: we can't reliably check malefic/benefic without functional classification
    { regex: /if\s+(malefic|benefic)\s+([A-Z][a-z]+)[.,;!?]*/gi, type: 'quality' },
    
    // "Planet with Planet or Planet in Xth House" (conjunction with OR)
    { regex: /([A-Z][a-z]+)\s+with\s+([A-Z][a-z]+)\s+or\s+([A-Z][a-z]+)\s+in\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)\s+House[.,;!?]*/gi, type: 'withOr' },
    
    // "Planet with Planet in Xth House" (conjunction)
    { regex: /([A-Z][a-z]+)\s+with\s+([A-Z][a-z]+)\s+in\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)\s+House[.,;!?]*/gi, type: 'with' },
    
    // "If Planet is placed with N planets in Xth House" (count-based)
    { regex: /if\s+(?:the\s+)?([A-Z][a-z]+)\s+is\s+placed\s+with\s+(\d+)\s+planets?\s+in\s+(?:the\s+)?([IVX]+|[0-9]+)(?:st|nd|rd|th)?\s+House[.,;!?]*/gi, type: 'withCount' },
    
    // "Planet placed in Xth House" (alternative phrasing)
    { regex: /([A-Z][a-z]+)\s+placed\s+in\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)\s+House[.,;!?]*/gi, type: 'placed' },
    
    // "if Planet in Xth House" or "if Planet in the Xth House"
    { regex: /if\s+(?:the\s+)?([A-Z][a-z]+)\s+(?:is\s+)?in\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)\s+House[.,;!?]*/gi, type: 'ifIn' },
    
    // "Planet and Planet in the Xth House"
    { regex: /([A-Z][a-z]+)\s+and\s+([A-Z][a-z]+)\s+in\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)\s+House[.,;!?]*/gi, type: 'and' },
    
    // "Planet in Xth House" (standalone - should be last to avoid over-matching)
    { regex: /([A-Z][a-z]+)\s+in\s+(?:the\s+)?(\d+)(?:st|nd|rd|th)\s+House[.,;!?]*/gi, type: 'in' },
  ];

  const segments: Array<{ text: string; isTrue?: boolean; isConditional?: boolean }> = [];
  let lastIndex = 0;

  // Find all matches across all patterns
  const allMatches: Array<{ match: RegExpExecArray; type: string; index: number; text: string }> = [];
  
  for (const pattern of patterns) {
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags); // Clone regex
    let match;
    while ((match = regex.exec(text)) !== null) {
      allMatches.push({ 
        match, 
        type: pattern.type, 
        index: match.index,
        text: match[0]
      });
    }
  }

  // Sort matches by position and remove overlaps (keep first match)
  allMatches.sort((a, b) => a.index - b.index);
  const uniqueMatches = [];
  let prevEnd = 0;
  for (const m of allMatches) {
    if (m.index >= prevEnd) {
      uniqueMatches.push(m);
      prevEnd = m.index + m.text.length;
    }
  }

  // Helper to convert Roman numerals
  const romanToArabic = (roman: string): number | null => {
    const romanMap: Record<string, number> = { I: 1, V: 5, X: 10 };
    let result = 0;
    for (let i = 0; i < roman.length; i++) {
      const current = romanMap[roman[i]];
      const next = romanMap[roman[i + 1]];
      if (next && current < next) {
        result -= current;
      } else {
        result += current;
      }
    }
    return result > 0 && result <= 12 ? result : null;
  };

  // Process each unique match
  for (const { match, type } of uniqueMatches) {
    // Add text before this match
    if (match.index > lastIndex) {
      segments.push({ text: text.substring(lastIndex, match.index) });
    }

    let isTrue = false;
    const matchedText = match[0];

    try {
      switch (type) {
        case 'afflicted': {
          // "if Planet is afflicted" or "if Planet is afflicted by Planet"
          const planet = match[1];
          const specificMalefic = match[2]; // Optional specific planet
          if (specificMalefic) {
            // Check if specific planet aspects the target planet
            const planetHouse = placementsD1[planet]?.house;
            if (!planetHouse || !allAspects) break;
            const aspectsToHouse = allAspects[planetHouse - 1] || [];
            isTrue = aspectsToHouse.some(asp => asp.from === specificMalefic);
          } else {
            // Check if ANY malefic aspects the planet
            isTrue = isPlanetAfflicted(planet);
          }
          break;
        }
        
        case 'afflictedPrefix': {
          // "afflicted Planet" (prefix format)
          const planet = match[1];
          isTrue = isPlanetAfflicted(planet);
          break;
        }
        
        case 'quality': {
          // "if malefic/benefic Planet" - skip functional check, just mark as conditional
          const planet = match[2];
          isTrue = placementsD1[planet] !== undefined; // True if planet exists in chart
          break;
        }

        case 'withOr': {
          // "Planet with Planet or Planet in Xth House"
          const planet1 = match[1];
          const planet2 = match[2];
          const planet3 = match[3];
          const house = parseInt(match[4], 10);
          const p1InHouse = placementsD1[planet1]?.house === house;
          const p2InHouse = placementsD1[planet2]?.house === house;
          const p3InHouse = placementsD1[planet3]?.house === house;
          isTrue = p1InHouse && (p2InHouse || p3InHouse);
          break;
        }

        case 'with': {
          // "Planet with Planet in Xth House"
          const planet1 = match[1];
          const planet2 = match[2];
          const house = parseInt(match[3], 10);
          isTrue = placementsD1[planet1]?.house === house && placementsD1[planet2]?.house === house;
          break;
        }

        case 'withCount': {
          // "If Planet is placed with N planets in Xth House"
          const planet = match[1];
          const count = parseInt(match[2], 10);
          const houseStr = match[3];
          const house = /^[IVX]+$/.test(houseStr) ? romanToArabic(houseStr) : parseInt(houseStr, 10);
          if (house && placementsD1[planet]?.house === house) {
            // Count other planets in same house
            const planetsInHouse = Object.entries(placementsD1).filter(
              ([p, data]) => p !== planet && data.house === house
            ).length;
            isTrue = planetsInHouse === count;
          }
          break;
        }

        case 'placed': {
          // "Planet placed in Xth House"
          const planet = match[1];
          const house = parseInt(match[2], 10);
          isTrue = placementsD1[planet]?.house === house;
          break;
        }

        case 'ifIn': {
          // "if Planet in Xth House"
          const planet = match[1];
          const house = parseInt(match[2], 10);
          isTrue = placementsD1[planet]?.house === house;
          break;
        }

        case 'and': {
          // "Planet and Planet in Xth House"
          const planet1 = match[1];
          const planet2 = match[2];
          const house = parseInt(match[3], 10);
          isTrue = placementsD1[planet1]?.house === house && placementsD1[planet2]?.house === house;
          break;
        }

        case 'in': {
          // "Planet in Xth House"
          const planet = match[1];
          const house = parseInt(match[2], 10);
          isTrue = placementsD1[planet]?.house === house;
          break;
        }
      }
    } catch (err) {
      console.warn('Error evaluating condition:', matchedText, err);
    }

    segments.push({
      text: matchedText,
      isTrue,
      isConditional: true
    });

    lastIndex = match.index + matchedText.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ text: text.substring(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ text }];
}

export default function HousePlanetInterpretations({ 
  ascLonD1, 
  cuspsD1, 
  grahaLon, 
  placementsD1, 
  getPlanetColor,
  combustMap,
  debilMap,
  exaltMap,
  blendedPct,
  houseScores,
  kaalSarpYoga,
  mangalDosha,
  sadeSati,
  conjunctions
}: HousePlanetInterpretationsProps) {
  const [rows, setRows] = useState<HouseRow[]>([]);
  const [allHouseAspects, setAllHouseAspects] = useState<ReturnType<typeof getPlanetsAspectingHouse>[]>([]);
  const [loadState, setLoadState] = useState<{ loading: boolean; error?: string } | null>(null);
  const tableRef = useRef<HTMLDivElement|null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        // Only show loading if we don't have cached data
        const hasCachedMeta = Boolean(MEMORY_CACHE.houseMeta && MEMORY_CACHE.planetMeta && MEMORY_CACHE.lordInHousePredictions);
        if (!hasCachedMeta) {
          setLoadState({ loading: true });
        }

        // Env guard similar to other components
        const envInfo: any = (typeof window !== 'undefined' ? (window as any).__SUPABASE_ENV : undefined);
        const hasUrl = Boolean(envInfo?.SUPABASE_URL || (process as any)?.env?.REACT_APP_SUPABASE_URL);
        const hasKey = Boolean(envInfo?.SUPABASE_ANON_KEY_SET || (process as any)?.env?.REACT_APP_SUPABASE_ANON_KEY);
        if (!hasUrl || !hasKey) {
          setLoadState({ loading: false, error: 'Supabase env not configured' });
          return;
        }

        const ascSign = ascSignFromAscLon(ascLonD1);

        // Ensure interpretation data is cached (localStorage + memory, 24-hour expiry)
        await ensureInterpretationDataCached();

        const houseMeta = MEMORY_CACHE.houseMeta!;
        const planetMeta = MEMORY_CACHE.planetMeta!;
        const lordInHousePredictions = MEMORY_CACHE.lordInHousePredictions!;

        // Build maps
  const houseMap = new Map<number, any>();
  houseMeta?.forEach((h: any) => { if (typeof h?.id === 'number') houseMap.set(h.id, h); });
        const planetIdByName = new Map<string, number>();
        planetMeta?.forEach((p: any) => { if (p?.name && typeof p?.id === 'number') planetIdByName.set(p.name, p.id); });

        // Precompute planets per house
        const housePlanets: Record<number, Planet[]> = Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i+1, []]));
        (Object.keys(placementsD1) as Planet[]).forEach((pl) => {
          const h = placementsD1[pl]?.house; if (!h) return;
          housePlanets[h].push(pl);
        });

        // Prepare all fetches for planet_in_house + lord_in_house
        type PIHKey = { planet: Planet; house: number };
        const pihPairs: PIHKey[] = [];
        for (let h = 1; h <= 12; h++) {
          for (const planet of housePlanets[h]) {
            if (!planetIdByName.has(planet)) continue;
            pihPairs.push({ planet, house: h });
          }
        }

        // Fetch planet_in_house rows - BATCH QUERY (not per-planet loops)
        const pihByKey = new Map<string, any>();
        
        if (pihPairs.length > 0) {
          const planetIds = [...new Set(pihPairs.map(p => planetIdByName.get(p.planet)).filter(Boolean))];
          
          // Single batched query for ALL planet-in-house data
          const { data: allPIH } = await supabase
            .from('planet_in_house_predictions')
            .select('*')
            .in('planet_id', planetIds);
          
          if (allPIH) {
            // Build lookup map
            const pihMap = new Map<string, any>();
            allPIH.forEach((row: any) => {
              const key = `${row.planet_id}:${row.house_id || 'null'}`;
              pihMap.set(key, row);
            });
            
            // Match each planet-house pair
            pihPairs.forEach(({ planet, house }) => {
              const pid = planetIdByName.get(planet);
              if (!pid) return;
              
              // Try exact match first
              const exactKey = `${pid}:${house}`;
              if (pihMap.has(exactKey)) {
                pihByKey.set(`${planet}:${house}`, pihMap.get(exactKey));
              } else {
                // Fallback to generic (house_id = null)
                const genericKey = `${pid}:null`;
                pihByKey.set(`${planet}:${house}`, pihMap.get(genericKey) || null);
              }
            });
          }
        }

        // Also fetch generic rows for all 7 planets (for sign lord nakshatra lookups) - already included in batch above
        PLANETS_7.forEach((planet) => {
          const pid = planetIdByName.get(planet);
          if (!pid) return;
          // Generic rows already fetched in batch query above
          const genericKey = `${planet}:0`;
          if (!pihByKey.has(genericKey)) {
            // Look for planet_id:null in the batch data
            const allPIH = Array.from(pihByKey.values());
            const genericRow = allPIH.find((r: any) => r?.planet_id === pid && !r?.house_id);
            pihByKey.set(genericKey, genericRow || null);
          }
        });

        // Use cached lord-in-house predictions
        const lihByKey = new Map<string, any>();
        // Populate map with all 12 lord houses for each destination house from cache
        lordInHousePredictions.forEach((row, destHouse) => {
          if (row) {
            // Create entries for all 12 lords pointing to this destination house
            for (let lordHouse = 1; lordHouse <= 12; lordHouse++) {
              lihByKey.set(`${lordHouse}->${destHouse}`, row);
            }
          }
        });

        // Pre-compute aspects for all 12 houses (needed for conditional text parsing)
        const allHouseAspects: ReturnType<typeof getPlanetsAspectingHouse>[] = [];
        for (let house = 1; house <= 12; house++) {
          allHouseAspects.push(getPlanetsAspectingHouse(house, placementsD1 as PlanetsInHouses, true));
        }
        
        // Build rows per house
        const next: HouseRow[] = [];
        for (let h = 1; h <= 12; h++) {
          const sId = houseSign(ascSign, h);
          const signName = RASHI_NAMES[sId - 1] as string;
          const meta = houseMap.get(h) || {};
          const signNoteColById: Record<number, string> = {
            1: 'note_sign_aries',
            2: 'note_sign_taurus',
            3: 'note_sign_gemini',
            4: 'note_sign_cancer',
            5: 'note_sign_leo',
            6: 'note_sign_virgo',
            7: 'note_sign_libra',
            8: 'note_sign_scorpio',
            9: 'note_sign_sagittarius',
            10: 'note_sign_capricorn',
            11: 'note_sign_aquarius',
            12: 'note_sign_pisces',
          };
          const signNote = meta?.[signNoteColById[sId]] ?? null;

          const planetsForHouse = housePlanets[h];
          
          // Detect Saturn conjunctions in this house
          const saturnInHouse = planetsForHouse.includes('Saturn');
          const saturnConjunctionMap = new Map<Planet, { conjunctWith: Planet; effect: string; score: number }>();
          
          if (saturnInHouse) {
            // Define Saturn conjunction effects
            const saturnEffects: Record<Planet, { effect: string; score: number }> = {
              Sun: { effect: '☀️ Saturn+Sun: Sunlight from south, iron windows, government connections', score: 20 },
              Moon: { effect: '🌙 Saturn+Moon: Mother\'s arthritis risk, food business success, mental challenges', score: -10 },
              Mars: { effect: '🔥 Saturn+Mars: Workplace conflicts, chronic disease risk, good for armed forces/technical work', score: -15 },
              Mercury: { effect: '📊 Saturn+Mercury: Entrepreneurship, trading success, commission work, documentation skills', score: 45 },
              Jupiter: { effect: '🎓 Saturn+Jupiter: Excellent teaching abilities, wisdom combined with discipline', score: 55 },
              Venus: { effect: '💎 Saturn+Venus: Wealth accumulation, textile/luxury goods industry success', score: 50 },
              Rahu: { effect: '🌍 Saturn+Rahu: Foreign success, unorthodox career, spiritual powers (siddhis)', score: 40 },
              Ketu: { effect: '🔮 Saturn+Ketu: Astrology/consulting excellence, spiritual blessings, detachment', score: 50 },
              Saturn: { effect: '', score: 0 }, // Saturn doesn't conjunct itself
            };
            
            for (const planet of planetsForHouse) {
              if (planet !== 'Saturn' && saturnEffects[planet]) {
                saturnConjunctionMap.set(planet, {
                  conjunctWith: 'Saturn',
                  effect: saturnEffects[planet].effect,
                  score: saturnEffects[planet].score
                });
                // Also mark Saturn as conjunct with this planet (mirror)
                saturnConjunctionMap.set('Saturn', {
                  conjunctWith: planet,
                  effect: saturnEffects[planet].effect,
                  score: saturnEffects[planet].score
                });
              }
            }
          }
          
          const planetsRows: HouseRow['planets'] = planetsForHouse.map((planet) => {
            const lon = grahaLon[planet]?.lon;
            const nakName = getNakshatraNameFromLon(lon);
            const pih = pihByKey.get(`${planet}:${h}`) || null;
            let nakText: string | null | undefined = undefined;
            let nakLordText: string | null | undefined = undefined;
            if (nakName && pih) {
              const colName = nakToColumn[nakName as keyof typeof nakToColumn];
              if (colName && pih[colName] != null) nakText = pih[colName] as string | null;
              const colLord = `${colName}_lord` as keyof typeof pih;
              if (pih && (colLord in pih) && (pih as any)[colLord] != null) {
                nakLordText = (pih as any)[colLord] as string | null;
              }
            }

            // This section is NOT used for planets in houses - removed old lordship logic
            // Lordship is handled separately at the house level (see below)

            // Planet-specific note from this house row
            const noteColByPlanet: Record<Planet, string> = {
              Sun: 'note_planet_sun',
              Moon: 'note_planet_moon',
              Mars: 'note_planet_mars',
              Mercury: 'note_planet_mercury',
              Jupiter: 'note_planet_jupiter',
              Venus: 'note_planet_venus',
              Saturn: 'note_planet_saturn',
              Rahu: 'note_planet_rahu',
              Ketu: 'note_planet_ketu',
            };
            const housePlanetNote: string | null = meta?.[noteColByPlanet[planet]] ?? null;

            // Compute dignities from existing maps
            const dignities: string[] = [];
            if (exaltMap?.[planet]) dignities.push('Exalted');
            if (debilMap?.[planet]) dignities.push('Debilitated');
            // CombustionMap only has Mercury, Venus, Mars, Jupiter, Saturn (not Sun, Moon, Rahu, Ketu)
            if (combustMap && planet !== 'Sun' && planet !== 'Moon' && planet !== 'Rahu' && planet !== 'Ketu') {
              const combustInfo = combustMap[planet as keyof typeof combustMap];
              if (combustInfo?.combust) dignities.push('Combust');
            }
            // Note: Own sign and Moolatrikona would need to be passed in separately or computed from signId

            return {
              planet,
              nakshatra: nakName,
              dignities,
              housePlanetNote,
              planetInHouse: pih ? { overview: pih?.overview, nakText, nakLordText } : null,
              lordInHouse: [], // Will be populated at house level, not planet level
              saturnConjunction: saturnConjunctionMap.get(planet) || null, // Add Saturn conjunction info
              blendedPct: blendedPct?.[planet], // Add blended percentage
            };
          });

          // NEW: Compute where THIS house's lord is placed
          // This is the correct perspective: "Lord of House X is in House Y"
          const thisHouseLord = SIGN_LORDS[sId - 1]; // Lord of the sign in this house
          const lordPlacement = placementsD1[thisHouseLord]?.house;
          const houseLordshipInfo: Array<{ lordHouseId: number; destHouse?: number; pred?: string | null; overview?: string | null; strengths?: string | null; challenges?: string | null; remedies?: string | null }> = [];
          
          if (lordPlacement) {
            // Fetch "Lord of house h is in house lordPlacement" from existing lihByKey
            const key = `${h}->${lordPlacement}`;
            const row = lihByKey.get(key);
            if (row) {
              // Extract the specific prediction for this lord
              const colName = `lord${h}_pred` as keyof typeof row;
              const pred = (row as any)[colName] ?? null;
              houseLordshipInfo.push({ 
                lordHouseId: h,
                destHouse: lordPlacement,
                pred, 
                overview: row?.overview, 
                strengths: row?.strengths, 
                challenges: row?.challenges, 
                remedies: row?.remedies 
              });
            }
          }

          // Compute aspecting planets for this house using canonical Parashari aspects
          const aspectingPlanets: Array<{ planet: Planet; nakshatra?: string }> = [];
          const houseAspects = getPlanetsAspectingHouse(h, placementsD1 as PlanetsInHouses, true);
          for (const aspect of houseAspects) {
            const planetLon = grahaLon[aspect.from]?.lon;
            const nakName = getNakshatraNameFromLon(planetLon);
            aspectingPlanets.push({ planet: aspect.from, nakshatra: nakName });
          }

          // Compute sign lord nakshatra for empty house analysis
          const signLord = SIGN_LORDS[sId - 1];
          const signLordLon = grahaLon[signLord]?.lon;
          let signLordNakshatra: string | null = null;
          let signLordNakshatraNote: string | null = null;
          
          if (signLordLon !== undefined) {
            const nakResult = getNakshatraNameFromLon(signLordLon);
            signLordNakshatra = nakResult ?? null;
            // Fetch generic planet_in_house_predictions for sign lord to get nakshatra note
            const signLordPih = pihByKey.get(`${signLord}:0`); // :0 for generic (house_id IS NULL)
            if (signLordNakshatra && signLordPih) {
              const nakCol = nakToColumn[signLordNakshatra as keyof typeof nakToColumn];
              if (nakCol && signLordPih[nakCol] != null) {
                signLordNakshatraNote = signLordPih[nakCol] as string | null;
              }
            }
          }

          // Build house-level predictions (actionable, caution, remedy) from houses table
          const housePredictions: HouseRow['housePredictions'] = {
            actionables: {},
            cautions: {},
            remedies: {},
          };
          
          const planetNames: Planet[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
          for (const planet of planetNames) {
            const planetLower = planet.toLowerCase();
            const actionableKey = `actionable_${planetLower}` as keyof typeof meta;
            const cautionKey = `caution_${planetLower}` as keyof typeof meta;
            const remedyKey = `remedy_${planetLower}` as keyof typeof meta;
            
            if (meta?.[actionableKey]) housePredictions.actionables[planet] = meta[actionableKey] as string;
            if (meta?.[cautionKey]) housePredictions.cautions[planet] = meta[cautionKey] as string;
            if (meta?.[remedyKey]) housePredictions.remedies[planet] = meta[remedyKey] as string;
          }

          next.push({
            house: h,
            signId: sId,
            signName,
            houseName: meta?.name,
            houseDescription: meta?.description ?? null,
            signInHouseNote: signNote,
            aspectingPlanets,
            houseLordshipInfo,
            planets: planetsRows,
            signLord,
            signLordNakshatra,
            signLordNakshatraNote,
            houseScore: houseScores?.[h], // Add house score
            housePredictions, // Add house predictions
          });
        }

        setRows(next);
        setAllHouseAspects(allHouseAspects);
        setLoadState({ loading: false });
      } catch (err: any) {
        console.error('[HousePlanetInterpretations] failed', err);
        setLoadState({ loading: false, error: err?.message || String(err) });
      }
    };
    run();
  }, [ascLonD1, cuspsD1, grahaLon, placementsD1]);

  function ordinal(n: number) {
    const s = ["th","st","nd","rd"], v = n % 100;
    return n + (s[(v-20)%10] || s[v] || s[0]);
  }

  async function copyText(text: string) {
    try { await navigator.clipboard.writeText(text); } catch {}
  }

  function buildPlanetRowText(house: number, p: NonNullable<HouseRow['planets']>[number]): string {
    const parts: string[] = [];
    let firstLine = `${p.planet} in ${ordinal(house)}` + (p.nakshatra ? ` (${p.nakshatra})` : '');
    if (p.dignities && p.dignities.length > 0) {
      firstLine += ` - ${p.dignities.join(', ')}`;
    }
    parts.push(firstLine);
    if (p.planetInHouse?.nakText) parts.push(`Nakshatra: ${p.planetInHouse.nakText}`);
    if (p.planetInHouse?.nakLordText) parts.push(`Nakshatra lord: ${p.planetInHouse.nakLordText}`);
    if (p.housePlanetNote) parts.push(`House note: ${p.housePlanetNote}`);
    if (p.planetInHouse?.overview) parts.push(`Planet in house: ${p.planetInHouse.overview}`);
    // Note: Lordship info is now at house level, not planet level
    return parts.join('\n');
  }

  function copyPlanetRow(house: number, p: NonNullable<HouseRow['planets']>[number]) {
    copyText(buildPlanetRowText(house, p));
  }

  function copyWholeTable() {
    const all: string[] = [];
    rows.forEach(r => {
      all.push(`House ${ordinal(r.house)} (${r.signName})`);
      if (r.signLord) {
        const lordInfo = `Sign Lord: ${r.signLord}` + (r.signLordNakshatra ? ` in ${r.signLordNakshatra}` : '');
        all.push(lordInfo);
        if (r.signLordNakshatraNote) all.push(`  ${r.signLordNakshatraNote}`);
      }
      if (r.houseDescription) all.push(`Description: ${r.houseDescription}`);
      if (r.signInHouseNote) all.push(`Sign note: ${r.signInHouseNote}`);
      r.planets.forEach(p => all.push(buildPlanetRowText(r.house, p)));
      all.push('');
    });
    copyText(all.join('\n'));
  }

  function exportPDFOfTable() {
    const node = tableRef.current; if (!node) return;
    const w = window.open('', '_blank'); if (!w) return;
    w.document.write('<html><head><title>My Chart – House Collation</title>');
    w.document.write('<style>body{font-family: system-ui, Segoe UI, Roboto, Helvetica, Arial; padding:12px} table{width:100%; border-collapse:collapse} th,td{border:1px solid #ddd; padding:8px; vertical-align:top} th{background:#f7f7f7}</style>');
    w.document.write('</head><body>');
    w.document.write(node.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.focus();
    w.print();
  }

  function copyLLMPrompt() {
    // Build comprehensive data structure for detailed report generation
    const houseData = rows.map(r => {
      // Determine primary interpretation source priority
      let primarySource = 'empty'; // empty, resident, aspecting, lordOnly
      if (r.planets.length > 0) {
        primarySource = 'resident';
      } else if (r.aspectingPlanets && r.aspectingPlanets.length > 0) {
        primarySource = 'aspecting';
      } else {
        primarySource = 'lordOnly';
      }

      return {
        house: r.house,
        houseName: r.houseName,
        houseDescription: r.houseDescription,
        sign: r.signName,
        signNote: r.signInHouseNote,
        signLord: r.signLord,
        signLordNakshatra: r.signLordNakshatra,
        signLordNakshatraNote: r.signLordNakshatraNote,
        primarySource,
        houseLordshipInfo: (r.houseLordshipInfo || []).map(li => ({
          lordOfHouse: li.lordHouseId,
          placedInWhichHouse: 'Check from data', // This will be in the prediction text
          prediction: li.pred,
          overview: li.overview,
          strengths: li.strengths,
          challenges: li.challenges,
          remedies: li.remedies
        })),
        residentPlanets: r.planets.map(p => ({
          planet: p.planet,
          nakshatra: p.nakshatra,
          dignities: p.dignities || [],
          nakshatraNote: p.planetInHouse?.nakText,
          nakshatraLordNote: p.planetInHouse?.nakLordText,
          houseNote: p.housePlanetNote,
          planetInHouseOverview: p.planetInHouse?.overview
        })),
        aspectingPlanets: (r.aspectingPlanets || []).map(ap => ({
          planet: ap.planet,
          nakshatra: ap.nakshatra
        }))
      };
    });

    // Build dosha/yoga summary
    const doshasSummary = [];
    if (kaalSarpYoga && (kaalSarpYoga.isPresent || kaalSarpYoga.present)) {
      doshasSummary.push({
        name: 'Kaal Sarp Yoga',
        severity: kaalSarpYoga.severity || 'unknown',
        details: kaalSarpYoga.details || `${kaalSarpYoga.specificType || kaalSarpYoga.type || 'Unknown type'}: ${kaalSarpYoga.specificEffects || 'All planets hemmed between Rahu and Ketu'}`,
        rahuHouse: kaalSarpYoga.rahuHouse,
        ketuHouse: kaalSarpYoga.ketuHouse
      });
    }
    if (mangalDosha && mangalDosha.present) {
      doshasSummary.push({
        name: 'Mangal Dosha (Kuja Dosha)',
        severity: mangalDosha.severity || 'unknown',
        details: `Mars in ${mangalDosha.houses?.join(', ') || 'dosha-producing'} house(s). Affects marriage compatibility and relationship harmony.`,
        houses: mangalDosha.houses
      });
    }
    if (sadeSati && sadeSati.isActive) {
      doshasSummary.push({
        name: 'Shani Sadesaati',
        severity: 'active',
        details: `Current Phase: ${sadeSati.currentPhase || 'Unknown'}. Active from ${sadeSati.from || 'N/A'} to ${sadeSati.to || 'N/A'}. Period of Saturn's discipline and karmic lessons.`,
        phase: sadeSati.currentPhase
      });
    }

    // Build house score summary with impact analysis
    const houseScoresSummary = Object.entries(houseScores || {})
      .filter(([, score]) => score !== undefined)
      .map(([house, score]) => {
        const scoreNum = Number(score);
        return {
          house: Number(house),
          score: scoreNum,
          impact: scoreNum >= 50 ? 'Highly Favorable' : 
                  scoreNum >= 20 ? 'Favorable' : 
                  scoreNum >= -20 ? 'Neutral' : 
                  scoreNum >= -50 ? 'Challenging' : 'Severely Afflicted',
          interpretation: scoreNum >= 50 ? 'Strong benefic influence - excellent prospects' :
                          scoreNum >= 20 ? 'Positive planetary support - good outcomes' :
                          scoreNum >= -20 ? 'Balanced energies - moderate results' :
                          scoreNum >= -50 ? 'Malefic influence present - needs remedies' :
                          'Heavy affliction - significant obstacles'
        };
      });

    // Build conjunction summary
    const conjunctionsSummary = (conjunctions || []).map(c => ({
      planets: c.planets.join(' + '),
      house: c.house,
      effect: c.effect || 'Combined planetary energies',
      score: c.score
    }));

    const prompt = `You are an expert Vedic astrologer creating a personalized, detailed birth chart report for a client. This report will be delivered to the end user (customer) and must be written in a warm, caring, and humanly manner.

CRITICAL TONE & LANGUAGE REQUIREMENTS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✓ Write in PERSONALIZED, WARM, and SIMPLE ENGLISH
✓ Use PRACTICAL, LIFE-RELEVANT language that resonates with real human experiences
✓ Avoid robotic, mechanical, telegram-style, or overly technical language
✓ Write as if you're a caring astrologer speaking directly to the person
✓ Use empathetic, conversational phrasing (e.g., "You have a natural gift for..." instead of "Native possesses...")
✓ Make it feel like guidance from a trusted advisor, not a cold analysis
✓ Be encouraging while being honest about challenges
✓ Include specific life examples and practical applications

INTERPRETATION PRIORITY SYSTEM:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
For each house, follow this layered approach:

1. PRIMARY FOCUS - Resident Planets (if present):
   - Start with the planet's placement, nakshatra, and dignities (Exalted, Debilitated, Combust, etc.)
   - Incorporate nakshatra notes and nakshatra lord notes
   - Add house-specific notes for that planet
   - Include lordship placements (e.g., "5th lord in 7th house")
   - Layer with the zodiac sign characteristics from the Sign column

2. SECONDARY FOCUS - Aspecting Planets (if house is empty):
   - Use planets from the "Planet Drishti" column
   - Mention their nakshatras
   - Explain how their aspects influence this empty house

3. FALLBACK - Sign Lord Information (if house is empty AND no aspects):
   - Focus on where the sign lord is placed
   - Include sign lord's nakshatra notes
   - Explain the connection between sign lord's placement and this house

4. ALWAYS LAYER IN - Zodiac Sign Context:
   - Weave in the zodiac sign's nature from the Sign column
   - Connect how the sign flavors the house matters

REQUIRED REPORT SECTIONS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Organize ALL information from the D1 House Collation table into these life sections:

1. PERSONALITY & FIRST IMPRESSIONS
   Focus on: 1st house (ascendant, physical appearance, natural temperament, how others perceive you, your approach to life, self-expression, core identity)
   This is the MOST important house for understanding the person's essential nature and how they present themselves to the world.

2. HEALTH & VITALITY
   Focus on: 1st house (body, vitality), 6th house (diseases, health challenges), 8th house (chronic issues, longevity), 12th house (hospitalization, rest)

3. WEALTH & FINANCES
   Focus on: 2nd house (savings, family wealth, speech), 11th house (gains, income sources), 5th house (speculation, investments), 9th house (fortune, prosperity)

4. CAREER, PROFESSION & WORK
   Focus on: 10th house (career, status, profession), 6th house (service, daily work, competition), 2nd house (earned income), 7th house (business partnerships), 3rd house (skills, efforts, business initiatives)
   Subdivide into:
   - Jobs & Employment
   - Business & Entrepreneurship  
   - Professional Fields & Work Nature

5. RELATIONSHIPS
   Subdivide into:
   - SPOUSE & PARTNERSHIPS: 7th house (marriage, spouse, business partners), 2nd house (family life), 8th house (in-laws, shared resources), 12th house (intimacy, bed pleasures)
   - CHILDREN & PROGENY: 5th house (children, creativity), 9th house (grandchildren, blessings)
   - FAMILY & SIBLINGS: 3rd house (siblings, neighbors), 4th house (mother, home), 9th house (father, teachers)

6. EDUCATION & LEARNING
   Focus on: 4th house (foundational education, mother's influence on learning), 5th house (higher education, intelligence, creative learning), 9th house (higher wisdom, philosophy, advanced degrees), 2nd house (early education, speech), 3rd house (skills, short courses)

7. SPIRITUAL LIFE & INNER GROWTH
   Focus on: 12th house (moksha, meditation, foreign lands), 9th house (dharma, philosophy, gurus), 8th house (occult, transformation), 5th house (past life merit, devotion)

8. HOME, PROPERTY & VEHICLES
   Focus on: 4th house (home, mother, property, vehicles, happiness), 2nd house (family base), 12th house (foreign property)

9. REMEDIES & RECOMMENDATIONS
   Group remedies by the sections above:
   - For Personality & First Impressions
   - For Health
   - For Wealth
   - For Career
   - For Relationships
   - For Education
   - For Spiritual Growth
   - General Remedies (any that don't fit specific sections)

CRITICAL INSTRUCTIONS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✗ DO NOT skip or ignore ANY data point from the table
✗ DO NOT use technical jargon without explanation
✗ DO NOT write in bullet points only - use flowing paragraphs with bullets for key points
✗ DO NOT sound like an AI or automated system
✗ DO NOT mention that you are "using text verbatim" or "as requested" or reference any instructions
✗ DO NOT make it obvious that this is AI-generated text following a template
✓ DO include EVERY nakshatra note, house note, and lordship placement
✓ DO explain the combined effects when multiple planets aspect or influence
✓ DO provide DETAILED interpretations (this is for a paying customer's report)
✓ DO make it conversational and personal throughout
✓ DO include specific examples of how each placement manifests in real life
✓ DO address the person directly using "you" and "your"
✓ DO weave in technical predictions naturally within flowing narrative
✓ DO present nakshatra notes, house notes, and predictions as if they are classical astrological wisdom, not verbatim quotes
✓ DO use phrases like "According to classical texts..." or "Vedic astrology suggests..." when incorporating technical interpretations
✓ DO integrate the data smoothly so it reads as a cohesive, professionally written astrology report

INTEGRATION STYLE FOR TECHNICAL DATA:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
When using nakshatra notes, house predictions, or lordship interpretations from the data:
- Blend them seamlessly into your narrative
- Frame them as classical astrological wisdom or observations
- Don't put them in quotes unless presenting them as classical scriptural references
- Make transitions smooth: "This placement indicates...", "The classical texts describe...", "This suggests..."
- Never say "The data shows..." or "As provided..." or "Verbatim text..."
- Write as if YOU are the astrologer interpreting the chart, not just transcribing data

DOSHAS & YOGAS PRESENT:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${doshasSummary.length > 0 ? JSON.stringify(doshasSummary, null, 2) : 'No major doshas detected in the chart.'}

IMPORTANT: Reference these doshas in the relevant life area sections (Health for Sadesaati, Relationships for Mangal Dosha, etc.)

HOUSE SCORES & STRENGTH ANALYSIS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${houseScoresSummary.length > 0 ? JSON.stringify(houseScoresSummary, null, 2) : 'House scores not available.'}

IMPORTANT: Use house scores to provide QUANTITATIVE assessment of each life area's strength. Mention specific scores when discussing prospects.

PLANET CONJUNCTIONS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${conjunctionsSummary.length > 0 ? JSON.stringify(conjunctionsSummary, null, 2) : 'No major conjunctions detected.'}

IMPORTANT: Integrate conjunction effects when discussing the relevant houses. Explain how the combined planetary energies manifest.

STRUCTURED CHART DATA:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${JSON.stringify(houseData, null, 2)}

Now, create a comprehensive, detailed, warm, and personalized Vedic astrology report following the above guidelines. Make it thorough enough to be a complete report section for the customer. Write as a professional astrologer presenting their expert analysis, NOT as an AI following instructions.`;

    copyText(prompt);
  }

  if (loadState?.error) {
    return (
      <Box sx={{ mt: 1, p: 1.25, borderRadius: 'sm', border: '1px dashed var(--joy-palette-neutral-outlinedBorder)', bgcolor: 'neutral.softBg', color: 'warning.700' }}>
        {loadState.error}
      </Box>
    );
  }

  if (loadState?.loading || !rows.length) {
    return (
      <Box sx={{ mt: 1, p: 1.25, borderRadius: 'sm', border: '1px dashed var(--joy-palette-neutral-outlinedBorder)', bgcolor: 'neutral.softBg' }}>
        Loading interpretations…
      </Box>
    );
  }

  const tableWrap: React.CSSProperties = {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    border: '1px solid #eee',
    borderRadius: 12,
    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
  };
  return (
    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <Typography level="h4" sx={{ fontWeight: 600 }}>D1 House Collation (planets, nakshatra, planet-in-house, lord-in-house)</Typography>
          <Tooltip title="Complete house-by-house breakdown of your chart. For each house (life area), see: the sign, which planets aspect it, planets placed in it with their nakshatras and interpretations, plus where that house's lord sits. Everything in one table." placement="top" arrow variant="soft" size="sm" sx={{ maxWidth: 280, p: 1, fontSize: '0.875rem' }}>
            <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
              <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="sm" variant="outlined" onClick={copyWholeTable} title="Copy whole table" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 1.5 } }}>Copy table</Button>
          <Button size="sm" variant="outlined" onClick={exportPDFOfTable} title="Export to PDF (browser print)" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 1.5 } }}>Export PDF</Button>
          <Button size="sm" variant="outlined" onClick={copyLLMPrompt} title="Copy AI summarization prompt" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 1.5 } }}>Copy AI prompt</Button>
        </Box>
      </Box>
      <Sheet variant="outlined" sx={{ overflowX: 'auto', borderRadius: 'sm', WebkitOverflowScrolling: 'touch' }} ref={tableRef}>
        <Table size="sm" stickyHeader sx={{ minWidth: { xs: 600, sm: 960 }, '& th, & td': { verticalAlign: 'top' }, '& th': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }, '& td': { fontSize: { xs: '0.75rem', sm: '0.875rem' } } }}>
          <thead>
            <tr>
              <th style={{ width: '15%' }}><Typography level="body-sm" sx={{ fontWeight: 600 }}>House</Typography></th>
              <th style={{ width: '12%' }}><Typography level="body-sm" sx={{ fontWeight: 600 }}>Sign</Typography></th>
              <th style={{ width: '18%' }}><Typography level="body-sm" sx={{ fontWeight: 600 }}>Planet Drishti</Typography></th>
              <th style={{ width: '55%' }}><Typography level="body-sm" sx={{ fontWeight: 600 }}>Planets + interpretations</Typography></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.house}>
                {/* Column 1: House - number, name, description, sign lord with nakshatra */}
                <td>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography level="body-md" sx={{ fontWeight: 600 }}>
                      {r.house}
                    </Typography>
                    {r.houseScore !== undefined && (
                      <Chip 
                        size="sm" 
                        variant="soft"
                        color={r.houseScore >= 0 ? 'success' : 'danger'}
                        sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                      >
                        {r.houseScore >= 0 ? '+' : ''}{r.houseScore.toFixed(1)}
                      </Chip>
                    )}
                  </Box>
                  {r.houseName && (
                    <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>{r.houseName}</Typography>
                  )}
                  {r.houseDescription && (
                    <Typography level="body-xs" sx={{ mb: 0.5 }}>{r.houseDescription}</Typography>
                  )}
                  {r.signLord && (
                    <Typography level="body-sm" sx={{ mt: 0.75, color: 'text.secondary' }}>
                      <Typography sx={{ fontWeight: 600 }}>Lord: </Typography>
                      <Typography sx={{ color: getPlanetColor ? getPlanetColor(r.signLord) : 'inherit' }}>
                        {r.signLord}
                      </Typography>
                      {r.signLordNakshatra && (
                        <Typography> in {r.signLordNakshatra}</Typography>
                      )}
                    </Typography>
                  )}
                  {r.signLordNakshatraNote && (
                    <Typography level="body-xs" sx={{ mt: 0.25, color: 'text.tertiary' }}>
                      {r.signLordNakshatraNote}
                    </Typography>
                  )}
                </td>

                {/* Column 2: Sign - name and sign note */}
                <td>
                  <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>{r.signName}</Typography>
                  {r.signInHouseNote && (
                    <Typography level="body-xs">
                      {r.signInHouseNote}
                    </Typography>
                  )}
                </td>

                {/* Column 3: Planet Drishti - planets aspecting this house */}
                <td>
                  {r.aspectingPlanets && r.aspectingPlanets.length > 0 ? (
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                      {r.aspectingPlanets.map((ap, idx) => (
                        <Typography component="li" level="body-xs" key={`${r.house}-aspect-${ap.planet}-${idx}`}>
                          <Typography sx={{ color: getPlanetColor ? getPlanetColor(ap.planet) : 'inherit', fontWeight: 600 }}>
                            {ap.planet}
                          </Typography>
                          {ap.nakshatra && <Typography> in {ap.nakshatra}</Typography>}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Typography level="body-xs" sx={{ fontStyle: 'italic' }}>—</Typography>
                  )}
                </td>

                {/* Column 4: Planets + interpretations (with dignities) */}
                <td>
                  {r.planets.length ? (
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                      {r.planets.map((p) => (
                        <Box component="li" key={`${r.house}-${p.planet}`} sx={{ mb: 1.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                              <Typography sx={{ color: getPlanetColor ? getPlanetColor(p.planet) : 'inherit' }}>{p.planet}</Typography>
                              {p.nakshatra ? ` in ${p.nakshatra}` : ''}
                              {p.dignities && p.dignities.length > 0 && (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 'normal' }}>
                                  {' '}({p.dignities.join(', ')})
                                </Typography>
                              )}
                            </Typography>
                            {p.blendedPct !== undefined && (
                              <Chip 
                                size="sm" 
                                variant="soft"
                                color={p.blendedPct >= 0 ? 'success' : 'danger'}
                                sx={{ fontSize: '0.6875rem', fontWeight: 600 }}
                              >
                                {p.blendedPct >= 0 ? '+' : ''}{p.blendedPct.toFixed(0)}%
                              </Chip>
                            )}
                            <Button
                              size="sm"
                              variant="plain"
                              onClick={() => copyPlanetRow(r.house, p)}
                              sx={{ ml: 'auto', minHeight: 0, px: 0.75, py: 0.25 }}
                              title="Copy this planet's summary"
                            >Copy</Button>
                          </Box>
                          {p.nakshatra && (
                            <Typography level="body-xs" sx={{ mt: 0.25 }}>
                              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Nakshatra: </Typography>
                              <Typography>{p.nakshatra}</Typography>
                              {p.planetInHouse?.nakText ? (
                                <>
                                  <Typography> — </Typography>
                                  <Typography>{p.planetInHouse.nakText}</Typography>
                                </>
                              ) : null}
                            </Typography>
                          )}
                          {p.planetInHouse?.overview && (
                            <Typography level="body-xs" sx={{ mt: 0.25 }}>
                              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Planet in house: </Typography>
                              <Typography component="span">
                                {parseConditionalText(p.planetInHouse.overview, placementsD1, r.house, allHouseAspects).map((segment, idx) => (
                                  <Typography
                                    key={idx}
                                    component="span"
                                    sx={{
                                      fontWeight: segment.isConditional && segment.isTrue ? 700 : 'normal',
                                      opacity: segment.isConditional && !segment.isTrue ? 0.4 : 1,
                                      color: segment.isConditional && segment.isTrue ? 'success.600' : 'inherit',
                                    }}
                                  >
                                    {segment.text}
                                  </Typography>
                                ))}
                              </Typography>
                            </Typography>
                          )}
                          {/* Keep lord note separate for clarity */}
                          {p.planetInHouse?.nakLordText && (
                            <Typography level="body-xs" sx={{ mt: 0.25 }}>
                              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Nakshatra lord: </Typography>
                              <Typography>{p.planetInHouse.nakLordText}</Typography>
                            </Typography>
                          )}
                          {p.housePlanetNote && (
                            <Typography level="body-xs" sx={{ mt: 0.25 }}>
                              <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Details: </Typography>
                              <Typography component="span">
                                {parseConditionalText(p.housePlanetNote, placementsD1, r.house, allHouseAspects).map((segment, idx) => (
                                  <Typography
                                    key={idx}
                                    component="span"
                                    sx={{
                                      fontWeight: segment.isConditional && segment.isTrue ? 700 : 'normal',
                                      opacity: segment.isConditional && !segment.isTrue ? 0.4 : 1,
                                      color: segment.isConditional && segment.isTrue ? 'success.600' : 'inherit',
                                    }}
                                  >
                                    {segment.text}
                                  </Typography>
                                ))}
                              </Typography>
                            </Typography>
                          )}
                          {/* Saturn Conjunction Chip */}
                          {p.saturnConjunction && (
                            <Sheet sx={{ mt: 0.75, p: 1, bgcolor: 'primary.softBg', borderLeft: '3px solid', borderColor: 'primary.500', borderRadius: 'sm' }}>
                              <Typography level="body-xs" sx={{ fontWeight: 600, color: 'primary.600', mb: 0.25 }}>
                                🪐 Saturn Conjunction Effect
                              </Typography>
                              <Typography level="body-xs" sx={{ mb: 0.25 }}>
                                {p.saturnConjunction.effect}
                              </Typography>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                Impact score: {p.saturnConjunction.score > 0 ? '+' : ''}{p.saturnConjunction.score}
                              </Typography>
                            </Sheet>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography level="body-xs" sx={{ fontStyle: 'italic' }}>No planets in this house.</Typography>
                  )}
                  
                  {/* House Lordship Info - shown at house level */}
                  {r.houseLordshipInfo && r.houseLordshipInfo.length > 0 && (
                    <Box sx={{ 
                      mt: r.planets.length > 0 ? 1.5 : 0, 
                      pt: r.planets.length > 0 ? 1.5 : 0, 
                      borderTop: r.planets.length > 0 ? '1px dashed' : 'none',
                      borderColor: 'divider'
                    }}>
                      {r.houseLordshipInfo.map((li) => (
                        <Box key={`house-lord-${r.house}-${li.lordHouseId}`} sx={{ mb: 1 }}>
                          <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {ordinal(li.lordHouseId)} Lord in {ordinal(li.destHouse || 0)} House
                          </Typography>
                          {li.pred && (
                            <Typography level="body-xs" sx={{ mb: 0.5 }} component="span">
                              {parseConditionalText(li.pred, placementsD1, r.house, allHouseAspects).map((segment, idx) => (
                                <Typography
                                  key={idx}
                                  component="span"
                                  sx={{
                                    fontWeight: segment.isConditional && segment.isTrue ? 700 : 'normal',
                                    opacity: segment.isConditional && !segment.isTrue ? 0.4 : 1,
                                    color: segment.isConditional && segment.isTrue ? 'success.600' : 'inherit',
                                  }}
                                >
                                  {segment.text}
                                </Typography>
                              ))}
                            </Typography>
                          )}
                          {li.overview && !li.pred && (
                            <Typography level="body-xs" sx={{ mb: 0.5 }} component="span">
                              {parseConditionalText(li.overview, placementsD1, r.house, allHouseAspects).map((segment, idx) => (
                                <Typography
                                  key={idx}
                                  component="span"
                                  sx={{
                                    fontWeight: segment.isConditional && segment.isTrue ? 700 : 'normal',
                                    opacity: segment.isConditional && !segment.isTrue ? 0.4 : 1,
                                    color: segment.isConditional && segment.isTrue ? 'success.600' : 'inherit',
                                  }}
                                >
                                  {segment.text}
                                </Typography>
                              ))}
                            </Typography>
                          )}
                          {(li.strengths || li.challenges || li.remedies) && (
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
                              {li.strengths && (
                                <Typography component="li" level="body-xs">
                                  <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Strengths: </Typography>
                                  <Typography>{li.strengths}</Typography>
                                </Typography>
                              )}
                              {li.challenges && (
                                <Typography component="li" level="body-xs">
                                  <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Challenges: </Typography>
                                  <Typography>{li.challenges}</Typography>
                                </Typography>
                              )}
                              {li.remedies && (
                                <Typography component="li" level="body-xs">
                                  <Typography sx={{ fontWeight: 600, color: 'text.secondary' }}>Remedies: </Typography>
                                  <Typography>{li.remedies}</Typography>
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                  {/* House-level Predictions (Actionable, Caution, Remedy) */}
                  {r.housePredictions && r.planets.length > 0 && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                      {r.planets.map((p) => {
                        const hasActionable = r.housePredictions?.actionables[p.planet];
                        const hasCaution = r.housePredictions?.cautions[p.planet];
                        const hasRemedy = r.housePredictions?.remedies[p.planet];
                        
                        if (!hasActionable && !hasCaution && !hasRemedy) return null;
                        
                        return (
                          <Box key={`pred-${r.house}-${p.planet}`} sx={{ mb: 1.5 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.75 }}>
                              <Typography sx={{ color: getPlanetColor ? getPlanetColor(p.planet) : 'inherit' }}>
                                {p.planet}
                              </Typography>
                              <Typography> Summary</Typography>
                            </Typography>
                            {hasActionable && (
                              <Sheet sx={{ mb: 0.5, p: 0.75, bgcolor: 'success.softBg', borderLeft: '3px solid', borderColor: 'success.500', borderRadius: 'sm' }}>
                                <Typography level="body-xs" sx={{ fontWeight: 600, color: 'success.700', mb: 0.25 }}>
                                  ✅ Actionable
                                </Typography>
                                <Typography level="body-xs">
                                  {hasActionable}
                                </Typography>
                              </Sheet>
                            )}
                            {hasCaution && (
                              <Sheet sx={{ mb: 0.5, p: 0.75, bgcolor: 'warning.softBg', borderLeft: '3px solid', borderColor: 'warning.500', borderRadius: 'sm' }}>
                                <Typography level="body-xs" sx={{ fontWeight: 600, color: 'warning.700', mb: 0.25 }}>
                                  ⚠️ Caution
                                </Typography>
                                <Typography level="body-xs">
                                  {hasCaution}
                                </Typography>
                              </Sheet>
                            )}
                            {hasRemedy && (
                              <Sheet sx={{ mb: 0.5, p: 0.75, bgcolor: 'primary.softBg', borderLeft: '3px solid', borderColor: 'primary.500', borderRadius: 'sm' }}>
                                <Typography level="body-xs" sx={{ fontWeight: 600, color: 'primary.700', mb: 0.25 }}>
                                  💡 Remedy
                                </Typography>
                                <Typography level="body-xs">
                                  {hasRemedy}
                                </Typography>
                              </Sheet>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>
      <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.tertiary' }}>
        Notes: all longitudes are sidereal (Lahiri). House system is whole sign (Ascendant sign = House 1).
      </Typography>
    </Box>
  );
}
// ===== END FILE: src/components/HousePlanetInterpretations.tsx =====